import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Users } from "lucide-react";
import { useApi } from "@/lib/api";
import { useSeo } from "@/lib/site";
import { Loading, NotFoundBlock } from "@/components/site/Loading";
import { ArticleCard } from "@/components/site/ArticleCard";
import { ServiceCard } from "@/components/site/ServiceCard";

const Step = ({ n, title, children, testId }) => (
  <section className="grid gap-4 md:grid-cols-[72px_1fr] py-10 border-t border-cream-deep" data-testid={testId}>
    <span className="font-serif text-3xl text-sage-dark" aria-hidden="true">{String(n).padStart(2, "0")}</span>
    <div><h2 className="text-2xl sm:text-3xl mb-4">{title}</h2>{children}</div>
  </section>
);

const List = ({ items }) => (
  <ul className="space-y-3 max-w-3xl">
    {items.map((t) => <li key={t} className="flex gap-3 text-ink-soft"><span className="mt-2.5 h-2 w-2 rounded-full bg-sage-dark shrink-0" aria-hidden="true" />{t}</li>)}
  </ul>
);

export default function PathwayPage() {
  const { slug } = useParams();
  const { data: p, loading, error } = useApi(`/pathways/${slug}`);
  useSeo(p?.label, p?.acknowledgement);
  if (loading) return <Loading />;
  if (error || !p || !p.considerations) return <NotFoundBlock title="We couldn't find that pathway." />;
  return (
    <article className="container-site pt-10 pb-8" data-testid="pathway-page">
      <Link to="/find-your-way" className="btn-ghost text-sm" data-testid="pathway-back-link"><ArrowLeft className="h-4 w-4" aria-hidden="true" /> All situations</Link>
      <header className="mt-8 max-w-4xl animate-fade-up">
        <p className="eyebrow mb-4">You chose</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="pathway-title">{p.label}</h1>
        <p className="mt-8 font-serif text-xl sm:text-2xl leading-relaxed text-ink border-l-4 border-sage pl-6" data-testid="pathway-acknowledgement">{p.acknowledgement}</p>
      </header>
      <div className="mt-12">
        <Step n={1} title="Understanding what's happening" testId="pathway-explanation"><p className="text-ink-soft text-lg max-w-3xl">{p.explanation}</p></Step>
        <Step n={2} title="Things to consider" testId="pathway-considerations"><List items={p.considerations} /></Step>
        <Step n={3} title="Possible next steps" testId="pathway-next-steps">
          <List items={p.next_steps} />
          {p.tools?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-3">
              {p.tools.map((t) => <Link key={t.slug} to={`/resources/${t.slug}`} className="btn-secondary !min-h-[44px] !py-2 text-sm" data-testid={`pathway-tool-${t.slug}`}><FileText className="h-4 w-4" aria-hidden="true" />{t.title}</Link>)}
            </div>
          )}
        </Step>
        <Step n={4} title="Read more" testId="pathway-articles">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{p.articles.map((a) => <ArticleCard key={a.slug} article={a} />)}</div>
        </Step>
        <Step n={5} title="Trusted UK support" testId="pathway-services">
          <div className="grid gap-5 md:grid-cols-2">{p.services.map((s) => <ServiceCard key={s.slug} entry={s} />)}</div>
        </Step>
        <Step n={6} title="Finding further human support" testId="pathway-human-support">
          <div className="card-soft p-6 md:p-8 flex gap-4 max-w-3xl">
            <Users className="h-6 w-6 text-teal shrink-0 mt-1" aria-hidden="true" />
            <div>
              <p className="text-ink-soft">{p.human_support}</p>
              <p className="mt-3 text-ink-soft">If anyone is in immediate danger, call <a href="tel:999" className="font-semibold underline">999</a>. Addiction &amp; Us is not an emergency service.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/directory" className="btn-primary !min-h-[44px] !py-2" data-testid="pathway-directory-btn">Browse the support directory</Link>
                <Link to="/urgent-help" className="btn-secondary !min-h-[44px] !py-2" data-testid="pathway-urgent-btn">Urgent help</Link>
              </div>
            </div>
          </div>
        </Step>
      </div>
    </article>
  );
}
